import { ROUTE_ACTION_PLAN } from '@/constants/routes';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { getRoute, getRouteLoadingScreen } from '@/utils';
import { useDisclosure } from '@cmpsr/components';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Intervention, InterventionStatus } from '../../types';

type DashboardPropsType = {
  interventions: Intervention[];
  onCompleteIntervention: (Intervention: Intervention) => void;
};

export default function useDashboardController({ interventions, onCompleteIntervention }: DashboardPropsType) {
  const BANNER_VISIBILITY_KEY = 'isActionPlanGuidanceBannerVisible';
  const [interventionToComplete, setInterventionToComplete] = useState<Intervention | null>(null);
  const [isActionPlanGuidanceBannerVisible, setIsActionPlanGuidanceBannerVisible] = useState(() => {
    const storedVisibility = localStorage.getItem(BANNER_VISIBILITY_KEY);
    return storedVisibility !== 'false';
  });
  const analytics = useAnalyticsCalls();
  const navigate = useNavigate();
  const { isOpen: isOpenCompleteModal, onClose: onCloseCompleteModal, onOpen: openCompleteModal } = useDisclosure();
  const { idIntervention } = useParams();

  const currentInterventions = interventions.filter(
    (intervention) => intervention.status === InterventionStatus.IN_PROGRESS
  );
  const completedCount = interventions.filter(
    (intervention) => intervention.status === InterventionStatus.COMPLETED
  ).length;
  const completedPercentage = Math.ceil((completedCount / (interventions.length || 1)) * 100);
  const allInterventionsCompleted =
    currentInterventions.length === 0 ||
    currentInterventions.every(({ status }) => status === InterventionStatus.COMPLETED);
  const filterInterventionsByStatus = (status: keyof typeof InterventionStatus): Intervention[] => {
    return interventions.filter(({ status: interventionStatus }) => interventionStatus === status);
  };

  const handleInterventionCompleted = (intervention: Intervention) => {
    const completedIntervention = interventions.find(
      (currentIntervention) => currentIntervention.id === intervention.id
    );

    if (completedIntervention) {
      completedIntervention.status = InterventionStatus.COMPLETED;
      completedIntervention.completedAt = new Date().toISOString();

      onCompleteIntervention(completedIntervention);
    }

    setInterventionToComplete(intervention);

    navigate(getRouteLoadingScreen(getRoute(ROUTE_ACTION_PLAN, { idIntervention: intervention.id })));

    analytics.callActionPlanItemCompleted(intervention.id, intervention.title);
  };

  useEffect(() => {
    if (idIntervention && interventions.length) {
      const intervention = interventions.find(({ id }) => id === idIntervention);
      if (intervention) {
        setInterventionToComplete(intervention);
        openCompleteModal();
      }
    }
  }, [idIntervention, interventions.length]);

  const handleCloseActionPlanGuidanceBanner = () => {
    setIsActionPlanGuidanceBannerVisible(false);
    localStorage.setItem(BANNER_VISIBILITY_KEY, 'false');
  };

  return {
    interventionToComplete,
    completedPercentage,
    handleInterventionCompleted,
    isOpenCompleteModal,
    onCloseCompleteModal,
    filterInterventionsByStatus,
    isActionPlanGuidanceBannerVisible,
    handleCloseActionPlanGuidanceBanner,
    allInterventionsCompleted,
  };
}
